/**
 * The component profile, resolved into what the UI actually shows.
 *
 * Two views render from this — the "Modules in use" stat tile and the
 * components card — and they must never disagree about how many modules are
 * on. Deriving both from one function is the only way to guarantee that.
 */

/**
 * One row per component, in profile order, with overrides already applied.
 *
 * The denominator is `configuredCiCount` — the devices that reported any module
 * configuration — not `totalCis`. Using the fleet total would understate every
 * component whenever some devices report nothing, reading "1 of 12" when the
 * honest statement is "1 of the 3 that told us".
 */
export function componentRows(project, { manualComponents } = {}) {
  const overrides = (project && project.componentOverrides) || {};

  // Manual project: modules come from the project's own issues, default ON.
  // Each entry is { component, provisionable } from the manual-components
  // endpoint; `provisionable: false` marks entries that are not separately
  // provisionable modules (base OS, install/upgrade, platform layers) — a
  // display flag only, it does not change the default-ON state.
  if (Array.isArray(manualComponents)) {
    return manualComponents.map(entry => {
      const name = typeof entry === 'string' ? entry : entry.component;
      const provisionable = typeof entry === 'string' ? true : entry.provisionable !== false;
      const override = overrides[name];
      const isOverridden = Boolean(override) && typeof override.enabled === 'boolean';
      return {
        component: name,
        enabled: isOverridden ? override.enabled : true,
        isOverridden,
        provisionable,
        manual: true
      };
    });
  }

  const snapshot = (project && project.inventorySnapshot) || {};
  const profile = Array.isArray(snapshot.componentProfile) ? snapshot.componentProfile : [];
  const configuredCiCount = snapshot.configuredCiCount || 0;

  const rows = profile.map(entry => {
    const override = overrides[entry.component];
    const isOverridden = Boolean(override) && typeof override.enabled === 'boolean';
    const enabledCiCount = entry.enabledCiCount || 0;
    return {
      component: entry.component,
      enabledCiCount,
      configuredCiCount,
      enabled: isOverridden ? override.enabled : enabledCiCount > 0,
      isOverridden,
      // "Off because we found none" is a different statement from "off because
      // you said so", and a reviewer deciding whether to trust the automation
      // needs to be able to tell them apart at a glance.
      autoOff: !isOverridden && enabledCiCount === 0,
      conflict: Boolean(override && override.conflict),
      inProfile: true
    };
  });

  // An override for a component the latest snapshot no longer lists still
  // governs marking, so it has to stay visible — dropping it would leave a
  // reviewer with a rule they cannot see, let alone undo.
  const orphans = Object.keys(overrides)
    .filter(name => !profile.some(e => e.component === name))
    .map(name => ({
      component: name,
      enabledCiCount: 0,
      configuredCiCount,
      enabled: Boolean(overrides[name].enabled),
      isOverridden: true,
      autoOff: false,
      conflict: Boolean(overrides[name].conflict),
      inProfile: false
    }));

  return rows.concat(orphans);
}

/** Counts for the "Modules in use" tile. */
export function moduleSummary(project) {
  const rows = componentRows(project);
  if (!rows.length) return null;
  const snapshot = (project && project.inventorySnapshot) || {};
  return {
    enabled: rows.filter(r => r.enabled).length,
    total: rows.length,
    configuredCiCount: snapshot.configuredCiCount || 0
  };
}
